import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useCommonFetch from "../../../../hooks/useCommonFetch";

import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../components/table/table";
import {
  scheduleInterviewList,
  updateJobApplicationBulk,
} from "../../../../services/workspace/recruitment.service";
import { Icons, SidebarIcons } from "../../../../utils/icons";
import CommonModal from "../../../components/modals/commonModel";
import CommonForm from "../../../components/form/form";
import BottomDrawer from "../../common/drawer/bottomDrawer";

const useStatusModal = () => {
  const [isOpen, setOpen] = useState(false);
  return {
    isOpen,
    open: () => setOpen(true),
    close: () => setOpen(false),
  };
};

const InterviewList = () => {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const shortlistModal = useStatusModal();
  const scheduleModal = useStatusModal();
  const holdModal = useStatusModal();
  const offerModal = useStatusModal();
  const hireModal = useStatusModal();
  const rejectModal = useStatusModal();

  const [isOpenSheet, setIsOpenSheet] = useState(false);

  const handleClose = () => setIsOpenSheet(false);
  const handleView = () => setIsOpenSheet(true);

  const actions_type = {
    VIEW: "VIEW",
    DELETE: "DELETE",
    ADD_EDIT: "ADD_EDIT",
  };

  const breadcrumbs = [{ label: "Interview Schedule", path: "" }];

  const TableHeaderData = [
    { label: "Candidate Name", key: "title" },
    { label: "Job Title Applied ", key: "job_name" },
    { label: "Interview Panel", key: "interview_panel" },
    { label: "Date", key: "date" },
    { label: "Time", key: "time" },
    { label: "Mode", key: "interview_mode" },
    { label: "Interviewer", key: "interviewer" },
    { label: "Status", key: "status" },
  ];

  const {
    data: tableData,
    total: totalData,
    currentPage,
    selectedId,
    action,
    handleSearchChange,
    handlePageChange,
    openModal,
    closeModal,
    handleCreate,
    handleUpdate,
    handleDelete,
    sortingUIProps,
  } = useCommonFetch(scheduleInterviewList, {
    dataPath: "jobInterview.data",
    totalPath: "jobInterview.meta.total",
    errorMessage: "Failed to fetch interviews!",
    debounceDelay: 500,
    customParams: {
      with: "jobApplication",
    },
    sortingOptions: [
      { label: "Candidate Name", field: "name" },
      { label: "Date", field: "date" },
    ],
    sortingModule: "interviewSchedule",
    orderParamNames: {
      orderByKey: "order_by",
      orderTypeKey: "order_type",
      includeDisabledKey: "includeDisabled",
    },
    sortingDisableText: "Disabled",
    sortingShowDisableToggle: false,
    dataTransformer: (data) => {
      const transformedData = data.map((item) => ({
        id: item.id,
        title: item.job_application.name,
        job_name: item.job_application?.job_opening_title || "-",
        interview_panel: item.panel,
        date: item.date,
        time: item.time,
        interview_mode: item.mode,
        notes: item.reason,
        interviewer: item.interviewer,
        status: {
          data: item?.status,
          className: "",
          variant: "status",
          customCases: customCase, // assuming you already have this defined somewhere
        },
        rawItem: item,
      }));

      const action = {
        view: (data) => {
          setSelectedItem(data);
          setIsOpenSheet(true);
        },
      };
      return {
        data: transformedData,
        action: action,
      };
    },
  });

  const STATUS = {
    PENDING: 0,
    SHORTLIST: 1,
    SCHEDULE: 2,
    HOLD: 3,
    OFFERED: 4,
    HIRE: 5,
    REJECT: 6,
  };

  const statusButtons = [

    {
      content: "On Hold",
      status: STATUS.HOLD,
      icon: Icons.HOLD,
      modal: holdModal,
    },
    {
      content: "Offer",
      status: STATUS.OFFERED,
      icon: Icons.OFFER,
      modal: offerModal,
    },
    {
      content: "Hired",
      status: STATUS.HIRE,
      icon: Icons.HIRE,
      modal: hireModal,
    },
    {
      content: "Rejected",
      status: STATUS.REJECT,
      icon: Icons.CLOSE,
      modal: rejectModal,
    },
  ];

  const actions = statusButtons.map((btn) => ({
    label: btn.label,
    icon: btn.icon,
    onClick: btn.modal.open,
    color: "text-primary-800",
    content: btn.content || "Action",
  }));

  const customCase = {
    0: {
      label: "Pending",
      color: "text-blue-600 text-sm",
      bg: "bg-blue-600",
    },
    1: {
      label: "Shortlisted",
      color: "text-green-600 text-sm",
      bg: "bg-green-600",
    },
    2: {
      label: "Scheduled",
      color: "text-indigo-600 text-sm",
      bg: "bg-indigo-600",
    },
    3: {
      label: "On Hold",
      color: "text-yellow-600 text-sm",
      bg: "bg-yellow-600",
    },
    4: {
      label: "Offered",
      color: "text-purple-600 text-sm",
      bg: "bg-purple-600",
    },
    5: {
      label: "Hired",
      color: "text-emerald-600 text-sm",
      bg: "bg-emerald-600",
    },
    6: {
      label: "Rejected",
      color: "text-red-600 text-sm",
      bg: "bg-red-600",
    },
  };

  const scheduleFields = [
    {
      name: "interview_panel",
      label: "Interview Round",
      type: "select",
      placeholder: "Select panel",
      options: [
        { label: "Round 1", value: "round1" },
        { label: "Round 2", value: "round2" },
      ],
      required: true,
      colSpan: 12,
      onChange: (value, formik) => {
        // Automatically set interview_mode when round changes
        formik.setFieldValue("interview_mode", "online");
      },
    },
    {
      name: "interview_mode",
      label: "Interview Mode",
      type: "select",
      placeholder: "Select mode",
      disabled: true, // cannot change manually
      options: [
        { label: "Online", value: "online" },
        { label: "Walk-in", value: "walk-in" },
      ],
      required: true,
      colSpan: 12,
    },
    {
      name: "interview_link",
      label: "Interview Link",
      type: "text",
      required: true,
      colSpan: 12,
    },
    {
      name: "interview_address",
      label: "Interview Address/Platform",
      type: "text",
      required: true,
      colSpan: 12,
    },

    {
      name: "interview_date",
      label: "Interview Date",
      type: "date",
      required: true,
      colSpan: 12,
    },
    {
      name: "interview_time",
      label: "Interview Time",
      type: "time",
      placeholder: "Select time",
      required: true,
      colSpan: 12,
    },
  ];

  const basicDetails = [
    // { label: "Applied For: ", value: selectedItem?.hear_from?.data },
    { label: "Last Role: ", value: selectedItem?.current_role?.data },
    {
      label: "Working/Worked at: ",
      value: selectedItem?.current_company?.data,
    },
    { label: "Location:", value: selectedItem?.address?.data },
    { label: "Email:", value: selectedItem?.email?.data },
    { label: "Phone: ", value: selectedItem?.phone_number?.data },
  ];

  const professionalDetails = [
    { label: "Total Experience: ", value: selectedItem?.experience?.data },
    {
      label: "Qualification: ",
      value: selectedItem?.education?.data,
    },
    { label: "Notice Period:", value: selectedItem?.notice_period?.data },
    { label: "Current Salary:", value: selectedItem?.current_ctc?.data },
    { label: "Expected Salary: ", value: selectedItem?.expected_ctc?.data },
    { label: "Key Skills: ", value: selectedItem?.key_skills?.data },
  ];
  const updateStatus = async (statusValue) => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one candidate.");
      return;
    }
    try {
      await updateJobApplicationBulk({
        job_application_uuids: selectedIds,
        status: statusValue,
        id: id,
      });

      toast.success("Status updated successfully.");
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      toast.error("Failed to update status.");
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <PageTableHeader
        isSearch
        shorting={true}
        onSearchChange={handleSearchChange}
        breadcrumbs={breadcrumbs}
        sortingModule="interviewSchedule"
        onSortingChange={sortingUIProps?.onSortingChange}
        sortingProps={sortingUIProps}
      />

      <CommonTable
        TableHeaderData={TableHeaderData}
        tabledata={tableData.data}
        currentPage={currentPage}
        totalData={totalData}
        setCurrentPage={handlePageChange}
        loading={false}
        action={tableData.action}
        selectionKey="id"
        selectedRows={selectedIds}
        onSelectionChange={setSelectedIds}
        actions={actions}
      />
      <BottomDrawer
        classNames={"!w-[600px]"}
        title="Candidate Details"
        isOpen={isOpenSheet}
        setIsOpen={setIsOpenSheet}
      >
        {selectedItem && (
          <div className=" flex flex-col gap-4">
            {/* HEADER */}
            <div className="flex items-center gap-4 flex-row justify-between text-theme">
              <div>
                <h3 className="text-xl font-semibold">
                  {selectedItem?.name?.data}
                </h3>

                <p className="text-sm text-gray-600">
                  {/* Applied: {selectedItem?.created_at || "-"} */}
                </p>
              </div>
              <span
                className={`text-xs px-3 py-1 rounded inline-block mt-2 ${
                  customCase[selectedItem?.status?.data]?.bg || "bg-gray-200"
                } text-white`}
              >
                {customCase[selectedItem?.status?.data]?.label}
              </span>
            </div>

            {/* BASIC DETAILS */}
            <div>
              <h2 className="text-theme font-semibold text-md border-b pb-2">
                Basic Details
              </h2>
              <div className="flex flex-col gap-2 p-2 pt-4">
                {basicDetails.map((item, idx) => (
                  <DetailCard key={idx} label={item.label} value={item.value} />
                ))}
              </div>
            </div>

            {/* PROFESSIONAL DETAILS */}
            <div>
              <h2 className="text-theme font-semibold text-md border-b pb-2">
                Professional Details
              </h2>
              <div className="flex flex-col gap-2 p-2">
                {professionalDetails.map((item, idx) => (
                  <DetailCard key={idx} label={item.label} value={item.value} />
                ))}
              </div>
            </div>
          </div>
        )}
      </BottomDrawer>
      <CommonModal
        show={scheduleModal.isOpen}
        onClose={scheduleModal.close}
        modalSize="md"
        headerTitle={"Interview Schedule"}
      >
        <CommonForm
          fields={scheduleFields}
          onSubmit={(values) => {
            schedule(STATUS.SCHEDULE, values);
            scheduleModal.close();
          }}
          submitButtonText="Schedule"
        />
      </CommonModal>
    </div>
  );
};

function DetailCard({ label, value }) {
  return (
    <div className="grid grid-cols-2 justify-between text-sm font-semibold">
      <span className="text-theme/60">{label}</span>
      <span className="font-medium text-theme">{value || "-"}</span>
    </div>
  );
}

export default InterviewList;
